import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { isPublicAuthPath } from '../../auth/publicAuthPaths';
import { ROUTES } from '../../constants/routes';
import Loader from '../common/Loader';
import { logAuthMount } from '../../auth/authLifecycleDebug';

/**
 * Catch-all for unknown routes. Clerk auth sub-routes must mount the same page components
 * as the dedicated /login/* and /signup/* routes — never a loader-only tree (remounts Clerk).
 */
const AuthFallback = () => {
  const { pathname } = useLocation();
  const { isAuthenticated, isLoaded } = useAuth();

  useEffect(() => {
    const cleanup = logAuthMount('AuthFallback');
    return cleanup;
  }, []);

  /*
   * Never mount second Login/Signup trees from catch-all — that remounts Clerk and re-sends OTP.
   * Canonical routes are /login/* and /signup/*; redirect there if we ever hit * on auth paths.
   */
  if (pathname.startsWith(ROUTES.SIGNUP)) {
    return <Navigate to={pathname} replace />;
  }

  if (pathname === ROUTES.LOGIN || pathname.startsWith(`${ROUTES.LOGIN}/`)) {
    return <Navigate to={pathname} replace />;
  }

  if (pathname === '/verify-email-address' || pathname.startsWith('/verify-email-address/')) {
    const suffix = pathname.replace(/^\/verify-email-address\/?/, '');
    const sub = suffix ? `/${suffix}` : '';
    return <Navigate to={`${ROUTES.SIGNUP}/verify-email-address${sub}`} replace />;
  }

  if (isPublicAuthPath(pathname)) {
    return <Navigate to={`${ROUTES.LOGIN}${pathname}`} replace />;
  }

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <Navigate to={ROUTES.LOGIN} replace />;
};

export default AuthFallback;
